/**
 * PrivacyPolicy — public-accessible legal document.
 *
 * Content reflects how FrameFlow actually handles user data today:
 * Supabase as the primary data processor (Postgres + Auth + Storage),
 * Razorpay for subscription payments. No advertising trackers, no
 * sharing with non-essential third parties. Plain language over legalese.
 *
 * Note for product owners: the boilerplate here is intended to be
 * factually accurate but is NOT a substitute for review by qualified
 * counsel before any production launch. Keep `effectiveDate` and
 * `lastUpdated` in sync when this document materially changes.
 */

import React from 'react';
import LegalShell from '../components/LegalShell';

const PrivacyPolicy = () => (
  <LegalShell
    eyebrow="Legal"
    title="Privacy Policy"
    effectiveDate="May 19, 2026"
    lastUpdated="September 23, 2026"
  >
    <p>
      This Privacy Policy explains what personal data FrameFlow (operated by
      Cinerian) collects when you use our film-production management platform,
      how we use it, who we share it with, and the choices you have. We aim
      to use as little personal data as possible to provide the service.
    </p>

    <h2>1. Who we are</h2>
    <p>
      &ldquo;FrameFlow&rdquo;, &ldquo;we&rdquo;, &ldquo;our&rdquo; and
      &ldquo;us&rdquo; refer to Cinerian, the company that operates the
      FrameFlow service. You can reach us at{' '}
      <a href="mailto:reach@cinerian.com">reach@cinerian.com</a> for any
      privacy-related question.
    </p>

    <h2>2. What we collect</h2>

    <h3>Account information</h3>
    <ul>
      <li>Email address (used as your login identifier)</li>
      <li>Name and profile photo (optional, only if you upload one)</li>
      <li>Password hash (stored by our auth provider; we never see the plaintext)</li>
      <li>Account role (free, pro, studio, etc.) and workspace memberships</li>
    </ul>

    <h3>Content you create</h3>
    <ul>
      <li>Workspaces, films, scenes, shots and the notes attached to them</li>
      <li>Scripts you upload (PDF / DOCX / TXT) and the parsed scene metadata derived from them</li>
      <li>
        Cast and crew records you add to a film, including names, roles,
        phone numbers and email addresses. When you send a call sheet, each
        person receives a private link and we record whether it was opened
        and confirmed. You are responsible for having their permission to
        share their contact details with us.
      </li>
      <li>Optional asset uploads such as shot reference images or workspace logos</li>
    </ul>

    <h3>Payment metadata</h3>
    <p>
      If you subscribe to a paid plan, our payment processor (Razorpay)
      handles your card details directly &mdash; we never see them. We do
      receive and store the resulting subscription identifier, plan, billing
      cycle, status and renewal date so we can grant the right access in your
      workspace.
    </p>

    <h3>Service logs</h3>
    <p>
      We retain short-lived server logs containing IP address, request path,
      response status, and timestamps. These are used only to debug issues,
      detect abuse, and meet security obligations.
    </p>

    <h2>3. How we use your data</h2>
    <ul>
      <li>To create and operate your account, workspaces and films</li>
      <li>To authenticate your sessions and enforce access permissions</li>
      <li>To process subscription payments and apply the corresponding plan limits</li>
      <li>To respond to your help-center messages and other support requests</li>
      <li>To send transactional notifications (e.g. subscription renewal, cancellation, password reset)</li>
      <li>To detect, prevent and respond to fraud or abuse of the service</li>
      <li>To comply with applicable laws and regulatory requests</li>
    </ul>
    <p>
      We do <strong>not</strong> use your data to train external AI models
      and we do <strong>not</strong> sell or rent your personal data to anyone.
    </p>

    <h2>4. Where your data is stored and who processes it</h2>
    <p>
      Your account data, content and metadata are stored with{' '}
      <strong>Supabase</strong> (our database, auth and storage provider).
      Payment metadata flows through <strong>Razorpay</strong>. We use a
      small number of additional processors, each only for the purpose
      named here:
    </p>
    <ul>
      <li>
        <strong>Google (Gemini)</strong> &mdash; when you run an AI script
        breakdown, analysis, shot suggestions or insights, the scene text
        involved is sent to Google&rsquo;s Gemini API to produce the result.
        We send only what the feature needs and do not use your scripts to
        train models.
      </li>
      <li>
        <strong>Resend</strong> &mdash; delivers transactional email:
        invitations, call sheets, daily production reports, billing and
        account notices.
      </li>
      <li>
        <strong>PostHog</strong> &mdash; product analytics, described in
        section 5.
      </li>
    </ul>
    <p>
      These providers are independent data processors with their own
      security programs. Data may be processed in regions outside India,
      including the United States; by using the service, you consent to
      such international transfers where lawful.
    </p>

    <h2>5. Cookies, local storage and analytics</h2>
    <p>
      FrameFlow uses cookies and browser local storage for strictly-necessary
      purposes: keeping you signed in, remembering your theme preference,
      persisting small UI choices, and &mdash; on the on-set coverage page
      &mdash; holding your updates on the device until the network returns.
      We do <strong>not</strong> use advertising cookies.
    </p>
    <p>
      We use <strong>PostHog</strong> to understand how the product is used
      so we can improve it. We record which features are used and when
      (for example &ldquo;a script was uploaded&rdquo;, &ldquo;a call sheet
      was sent&rdquo;), along with your account id, workspace id, plan and
      basic device information. We do <strong>not</strong> record your
      script text, scene content, cast or crew details, or the contents of
      anything you type, and we do not use session recording or
      screen replay. Analytics data is processed by PostHog in the United
      States. You can ask us to exclude your account from analytics by
      emailing the address in section 12.
    </p>

    <h2>6. Sharing</h2>
    <p>We share personal data only in these narrow situations:</p>
    <ul>
      <li>
        <strong>With our processors</strong> (Supabase, Razorpay, Google
        Gemini, Resend, PostHog) strictly to provide and improve the
        service, as described in sections 4 and 5.
      </li>
      <li>
        <strong>Within your workspace</strong>, so collaborators you invite
        can see the content you create there.
      </li>
      <li>
        <strong>With the cast and crew you send a call sheet to.</strong>{' '}
        Each recipient&rsquo;s private link shows the shoot day&rsquo;s
        schedule, locations, notes and the names, roles and call times of
        the other people called that day. It does not show other
        people&rsquo;s phone numbers or email addresses. Links expire a few
        days after the shoot.
      </li>
      <li>
        <strong>When required by law</strong>, for example to respond to a
        valid court order or regulatory request.
      </li>
      <li>
        <strong>In the event of a corporate transaction</strong> (merger,
        acquisition, sale of assets), in which case we will notify you and
        any successor will be bound by this policy.
      </li>
    </ul>

    <h2>7. Your rights</h2>
    <p>Regardless of where you live, you can:</p>
    <ul>
      <li>Access the personal data we hold about you</li>
      <li>Correct inaccurate data via your profile settings</li>
      <li>Request a copy or export of your workspace content</li>
      <li>Delete your account &mdash; this permanently removes your profile and any workspaces you solely own</li>
      <li>Withdraw consent at any time where consent was the legal basis</li>
    </ul>
    <p>
      To exercise any of these rights, write to{' '}
      <a href="mailto:reach@cinerian.com">reach@cinerian.com</a>. We aim to
      respond within 30 days.
    </p>

    <h2>8. Data retention</h2>
    <p>
      We keep account data for as long as your account is active. When you
      delete your account, your profile and the workspaces you solely own
      are removed within 30 days, except where we are required by law to
      retain certain records (for example, tax-related invoices, retained
      for up to 7 years). Service logs are rotated within 90 days.
    </p>

    <h2>9. Security</h2>
    <p>
      We use industry-standard safeguards: TLS for all network traffic, JWT
      session tokens, rate limiting on sensitive endpoints, HMAC verification
      on payment webhooks, and least-privilege access controls on our own
      tooling. No system is perfectly secure; if you believe your account has
      been compromised, contact{' '}
      <a href="mailto:support@cinerian.com">support@cinerian.com</a> immediately.
    </p>

    <h2>10. Children</h2>
    <p>
      FrameFlow is not intended for use by children under 16. We do not
      knowingly collect personal data from anyone under that age; if you
      believe a child has provided us with personal data, contact us and we
      will delete it.
    </p>

    <h2>11. Changes to this policy</h2>
    <p>
      We may update this policy from time to time. Material changes will be
      announced inside the product and via email (for paid customers) at
      least 14 days before they take effect. The &ldquo;Last updated&rdquo;
      date at the top of this page always reflects the current version.
    </p>

    <h2>12. Contact</h2>
    <p>
      Questions, complaints or requests under this policy:{' '}
      <a href="mailto:reach@cinerian.com">reach@cinerian.com</a>
      <br />
      Support and account issues:{' '}
      <a href="mailto:support@cinerian.com">support@cinerian.com</a>
    </p>
  </LegalShell>
);

export default PrivacyPolicy;
